import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent, scheduleDocumentScrollLockSync } from '@theme/dialog';

/**
 * A specialist product-media viewer with predictable focus return, keyboard
 * navigation, native horizontal swiping and guarded scroll-lock cleanup.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnail rail.
 * @property {HTMLElement} gallery - The zoomed media carousel.
 * @property {HTMLElement} [counter] - Current media counter.
 * @property {HTMLButtonElement} [previousButton] - Previous media control.
 * @property {HTMLButtonElement} [nextButton] - Next media control.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails', 'gallery'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());
  #highResImagesLoading = /** @type {Set<string>} */ (new Set());
  #highResImagesPrefetched = /** @type {Set<string>} */ (new Set());
  /** @type {HTMLElement | null} */
  #previouslyFocusedElement = null;
  #activeIndex = 0;
  #isClosing = false;
  #backdropPointerStartedOutside = false;

  connectedCallback() {
    super.connectedCallback();
    this.refs.gallery.addEventListener('scroll', this.handleScroll);
    this.refs.dialog.addEventListener('cancel', this.#handleCancel);
    this.refs.dialog.addEventListener('close', this.#handleNativeClose);
    this.refs.dialog.addEventListener('pointerdown', this.#handlePointerDown, { passive: true });
    this.refs.dialog.addEventListener('pointerup', this.#handlePointerUp, { passive: true });
    this.#updateSelection(0, { dispatch: false });
  }

  disconnectedCallback() {
    this.refs.gallery.removeEventListener('scroll', this.handleScroll);
    this.refs.dialog.removeEventListener('cancel', this.#handleCancel);
    this.refs.dialog.removeEventListener('close', this.#handleNativeClose);
    this.refs.dialog.removeEventListener('pointerdown', this.#handlePointerDown);
    this.refs.dialog.removeEventListener('pointerup', this.#handlePointerUp);

    this.#resetZoomWrappers(false);

    if (this.refs.dialog.open) {
      this.refs.dialog.classList.remove('dialog--closed');
      this.refs.dialog.close();
    }

    this.#previouslyFocusedElement = null;
    this.#isClosing = false;
    this.#backdropPointerStartedOutside = false;
    scheduleDocumentScrollLockSync();
    super.disconnectedCallback();
  }

  #handleCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  #handlePointerDown = (event) => {
    this.refs.dialog.dataset.inputModality = 'pointer';
    this.#backdropPointerStartedOutside = this.#isBackdropPointer(event);
  };

  #handlePointerUp = (event) => {
    const shouldClose = this.#backdropPointerStartedOutside && this.#isBackdropPointer(event);
    this.#backdropPointerStartedOutside = false;

    if (shouldClose) this.close();
  };

  /** @param {PointerEvent} event */
  #isBackdropPointer(event) {
    if (event.target !== this.refs.dialog) return false;

    const rect = this.refs.dialog.getBoundingClientRect();
    return (
      event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom
    );
  }

  /** @param {boolean} expanded */
  #syncOpenerExpanded(expanded) {
    const opener = this.#previouslyFocusedElement;
    const { dialog } = this.refs;
    if (!(opener instanceof HTMLElement)) return;

    const controlledIds = (opener.getAttribute('aria-controls') || '')
      .split(/\s+/)
      .filter(Boolean);

    if ((dialog.id && controlledIds.includes(dialog.id)) || (this.id && controlledIds.includes(this.id))) {
      opener.setAttribute('aria-expanded', String(expanded));
    }
  }

  #handleNativeClose = () => {
    this.#syncOpenerExpanded(false);
    this.refs.dialog.classList.remove('dialog--closed');
    this.#isClosing = false;
    this.#backdropPointerStartedOutside = false;
    this.#resetZoomWrappers(false);
    scheduleDocumentScrollLockSync();

    const previous = this.#previouslyFocusedElement;
    this.#previouslyFocusedElement = null;

    if (!(previous instanceof HTMLElement) || !previous.isConnected) return;

    window.requestAnimationFrame(() => {
      const active = document.activeElement;
      const shouldRestore =
        !active ||
        active === document.body ||
        active === document.documentElement ||
        this.refs.dialog.contains(active);
      const isVisible = previous.getClientRects().length > 0 && !previous.closest('[inert]');
      const isDisabled = 'disabled' in previous && Boolean(previous.disabled);

      if (shouldRestore && isVisible && !isDisabled) {
        previous.focus({ preventScroll: true });
      }
    });
  };

  /**
   * Opens the zoom dialog.
   * @param {number} index
   * @param {PointerEvent | MouseEvent | KeyboardEvent} event
   */
  async open(index, event) {
    event.preventDefault();

    const safeIndex = this.#normaliseIndex(index);
    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[safeIndex];
    const targetThumbnail = thumbnails.children[safeIndex];
    const opener = event.target instanceof HTMLElement ? event.target : null;

    this.#previouslyFocusedElement = opener ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    this.#isClosing = false;
    dialog.dataset.inputModality = event instanceof KeyboardEvent || (event instanceof MouseEvent && event.detail === 0)
      ? 'keyboard'
      : 'pointer';
    dialog.classList.remove('dialog--closed');
    this.#resetZoomWrappers(false);
    this.#updateSelection(safeIndex, { dispatch: false });

    const openDialog = () => {
      if (!dialog.open) dialog.showModal();
      this.#syncOpenerExpanded(true);
      scheduleDocumentScrollLockSync();

      targetThumbnail?.scrollIntoView({
        behavior: 'instant',
        block: 'nearest',
        inline: 'center',
      });

      this.refs.gallery.scrollTo({
        left: targetImage?.offsetLeft ?? 0,
        behavior: 'instant',
      });
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions || !sourceImage || !targetImage || prefersReducedMotion()) {
      openDialog();
      this.#activateMedia(safeIndex);
      return;
    }

    const transitionName = 'gallery-item';
    sourceImage.style.setProperty('view-transition-name', transitionName);

    try {
      await startViewTransition(() => {
        openDialog();
        sourceImage.style.removeProperty('view-transition-name');
        targetImage.style.setProperty('view-transition-name', transitionName);
      });
    } catch (error) {
      // View transitions are progressive enhancement. A rejected transition
      // must never prevent the gallery from opening.
      openDialog();
    } finally {
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.removeProperty('view-transition-name');
    }

    this.#activateMedia(safeIndex);
  }

  /**
   * Loads a high-resolution image while preserving the original image node,
   * dimensions, accessibility attributes and magnifier references.
   * @param {HTMLElement} mediaContainer
   */
  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (
      !highResolutionUrl ||
      this.#highResImagesLoaded.has(highResolutionUrl) ||
      this.#highResImagesLoading.has(highResolutionUrl)
    ) {
      return false;
    }

    this.#highResImagesLoading.add(highResolutionUrl);
    preloadImage(highResolutionUrl);

    const preloader = new Image();
    preloader.onload = () => {
      this.#highResImagesLoading.delete(highResolutionUrl);
      if (!image.isConnected) return;

      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = highResolutionUrl;
      this.#highResImagesLoaded.add(highResolutionUrl);
    };
    preloader.onerror = () => {
      this.#highResImagesLoading.delete(highResolutionUrl);
    };
    preloader.src = highResolutionUrl;
    return true;
  }

  /** @param {number} index */
  #prefetchAdjacentImages(index) {
    const neighbours = [index - 1, index + 1];

    for (const neighbourIndex of neighbours) {
      const mediaContainer = this.refs.media[neighbourIndex];
      if (!mediaContainer?.classList.contains('product-media-container--image')) continue;

      const image = mediaContainer.querySelector('img.product-media__image');
      const url = image?.getAttribute('data_max_resolution');
      if (!url || this.#highResImagesPrefetched.has(url) || this.#highResImagesLoaded.has(url)) continue;

      this.#highResImagesPrefetched.add(url);
      preloadImage(url);
    }
  }

  /** Update the active media as the main stage scrolls or swipes. */
  handleScroll = debounce(() => {
    const { media, gallery } = this.refs;
    const mostVisibleElement = getMostVisibleElement(media, gallery);
    if (!mostVisibleElement) return;

    const activeIndex = media.indexOf(mostVisibleElement);
    if (activeIndex < 0) return;

    if (activeIndex !== this.#activeIndex) {
      this.#resetZoomWrappers(false);
    }

    this.#activateMedia(activeIndex);
  }, 60);

  /** Closes the zoom dialog. */
  async close() {
    const { dialog, media, thumbnails, gallery } = this.refs;
    if (!dialog.open || this.#isClosing) return;

    this.#isClosing = true;
    this.#resetZoomWrappers(true);
    const mostVisibleElement = getMostVisibleElement(media, gallery);

    if (!supportsViewTransitions || !mostVisibleElement || prefersReducedMotion()) {
      this.closeDialog();
      return;
    }

    const activeIndex = media.indexOf(mostVisibleElement);
    const transitionName = 'gallery-item';

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';
    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) {
      this.closeDialog();
      return;
    }

    dialog.classList.add('dialog--closed');

    try {
      await onAnimationEnd(thumbnails);
      mostVisibleElement.style.setProperty('view-transition-name', transitionName);

      await startViewTransition(() => {
        mostVisibleElement.style.removeProperty('view-transition-name');
        slide.style.setProperty('view-transition-name', transitionName);
        this.closeDialog();
      });
    } catch (error) {
      // Never leave the top-layer dialog or scroll lock active because a view
      // transition was interrupted by navigation, resizing or browser state.
      this.closeDialog();
    } finally {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.removeProperty('view-transition-name');
      dialog.classList.remove('dialog--closed');
      this.#isClosing = false;
    }
  }

  closeDialog() {
    const { dialog } = this.refs;
    if (!dialog.open) {
      this.#isClosing = false;
      scheduleDocumentScrollLockSync();
      return;
    }

    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
    scheduleDocumentScrollLockSync();
  }

  /** @param {KeyboardEvent} event */
  handleKeyDown(event) {
    this.refs.dialog.dataset.inputModality = 'keyboard';

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    const target = event.target;
    if (
      target instanceof HTMLElement &&
      target.matches('input, textarea, select, [contenteditable="true"], video, model-viewer')
    ) {
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previous();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    } else if (event.key === 'Home') {
      event.preventDefault();
      this.selectThumbnail(0, { behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
    } else if (event.key === 'End') {
      event.preventDefault();
      this.selectThumbnail(this.refs.media.length - 1, { behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
    }
  }

  previous() {
    if (this.#activeIndex <= 0) return;
    this.selectThumbnail(this.#activeIndex - 1, { behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
  }

  next() {
    if (this.#activeIndex >= this.refs.media.length - 1) return;
    this.selectThumbnail(this.#activeIndex + 1, { behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
  }

  /** @param {number} index */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /** @param {number} index */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.#loadHighResolutionImage(media[index]);
  }

  /**
   * @param {number} index
   * @param {{ behavior: ScrollBehavior }} options
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.media.length) return;

    const safeIndex = this.#normaliseIndex(index);
    const { media, thumbnails, gallery } = this.refs;
    const targetThumbnail = thumbnails.children[safeIndex];
    const targetImage = media[safeIndex];

    if (!(targetThumbnail instanceof HTMLElement) || !targetImage) return;

    this.#resetZoomWrappers(true);
    this.#updateSelection(safeIndex, { dispatch: false });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    gallery.scrollTo({
      left: targetImage.offsetLeft,
      behavior: options.behavior,
    });

    this.#activateMedia(safeIndex);
  }

  /** @param {number} index */
  #activateMedia(index) {
    this.#updateSelection(index, { dispatch: true });
    const mediaContainer = this.refs.media[index];
    if (mediaContainer) this.#loadHighResolutionImage(mediaContainer);
    this.#prefetchAdjacentImages(index);
  }

  /**
   * @param {number} index
   * @param {{ dispatch: boolean }} options
   */
  #updateSelection(index, options) {
    const safeIndex = this.#normaliseIndex(index);
    this.#activeIndex = safeIndex;

    Array.from(this.refs.thumbnails.querySelectorAll('button')).forEach((button, buttonIndex) => {
      button.setAttribute('aria-current', `${buttonIndex === safeIndex}`);
      button.toggleAttribute('data-active', buttonIndex === safeIndex);
    });

    this.refs.media.forEach((item, mediaIndex) => {
      if (mediaIndex === safeIndex) {
        item.setAttribute('aria-current', 'true');
      } else {
        item.removeAttribute('aria-current');
      }
    });

    if (this.refs.counter) {
      this.refs.counter.textContent = `${safeIndex + 1} of ${this.refs.media.length}`;
    }

    if (this.refs.previousButton) {
      this.refs.previousButton.disabled = safeIndex === 0;
    }

    if (this.refs.nextButton) {
      this.refs.nextButton.disabled = safeIndex === this.refs.media.length - 1;
    }

    if (options.dispatch) {
      this.dispatchEvent(new ZoomMediaSelectedEvent(safeIndex));
    }
  }

  /** @param {number} index */
  #normaliseIndex(index) {
    if (!Number.isFinite(index) || this.refs.media.length === 0) return 0;
    return Math.min(Math.max(Math.trunc(index), 0), this.refs.media.length - 1);
  }

  /** @param {boolean} animate */
  #resetZoomWrappers(animate) {
    this.querySelectorAll('drag-zoom-wrapper').forEach((wrapper) => {
      if ('resetZoom' in wrapper && typeof wrapper.resetZoom === 'function') {
        wrapper.resetZoom({ animate });
      }
    });
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Resolve the currently most visible media item synchronously. This avoids an
 * IntersectionObserver promise remaining unresolved while a dialog is being
 * closed, removed or moved into a view transition.
 * @param {HTMLElement[]} elements
 * @param {HTMLElement} container
 * @returns {HTMLElement | null}
 */
function getMostVisibleElement(elements, container) {
  if (!elements.length) return null;

  const containerRect = container.getBoundingClientRect();
  let bestElement = elements[0];
  let bestScore = -1;

  for (const element of elements) {
    const rect = element.getBoundingClientRect();
    const overlapWidth = Math.max(0, Math.min(rect.right, containerRect.right) - Math.max(rect.left, containerRect.left));
    const overlapHeight = Math.max(0, Math.min(rect.bottom, containerRect.bottom) - Math.max(rect.top, containerRect.top));
    const visibleArea = overlapWidth * overlapHeight;
    const totalArea = Math.max(1, rect.width * rect.height);
    const score = visibleArea / totalArea;

    if (score > bestScore) {
      bestScore = score;
      bestElement = element;
    }
  }

  return bestElement;
}
